import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AiSuggestedBadge } from './import/AiSuggestedBadge';
import { Modal } from './ui/Modal';
import apiService from '../services/ApiService';
import type { ModificationEditOperation } from '../types/modificationAiEdit';

interface AiEditModificationsModalProps {
  isOpen: boolean;
  vehicleId: number;
  onClose: () => void;
  onApplied?: () => void;
}

type Phase = 'compose' | 'analyzing' | 'clarify' | 'review' | 'applying' | 'done' | 'error';

const MAX_INSTRUCTION = 2000;

const EXAMPLES: { label: string; text: string }[] = [
  { label: 'Move parts', text: 'make the MIVEC head a child of the 2.2L engine mod' },
  { label: 'Clean up categories', text: 'move all Motec parts to Electronics' },
  { label: 'Add a part', text: 'add a custom exhaust made by P&L Auto' },
  { label: 'Remove parts', text: 'remove all suspension mods' },
  { label: 'Update specifications', text: 'the Regamasters are 18x9.5 +22' },
];

/** Review groups keep a long plan scannable instead of one flat list. */
const OP_GROUPS: { id: string; label: string; types: string[] }[] = [
  { id: 'add', label: 'Additions', types: ['add'] },
  { id: 'hierarchy', label: 'Hierarchy', types: ['move', 'bulk_move'] },
  { id: 'category', label: 'Categories', types: ['change_category', 'bulk_category'] },
  { id: 'details', label: 'Details', types: ['update'] },
  {
    id: 'specs',
    label: 'Specifications',
    types: ['add_specification', 'update_specification', 'remove_specification'],
  },
  { id: 'removals', label: 'Removals', types: ['delete', 'bulk_delete'] },
];

const SUMMARY_LABELS: Record<string, (n: number) => string> = {
  added: (n) => `${n} ${n === 1 ? 'part' : 'parts'} added`,
  updated: (n) => `${n} ${n === 1 ? 'part' : 'parts'} updated`,
  moved: (n) => `${n} ${n === 1 ? 'part' : 'parts'} moved`,
  category_changed: (n) => `${n} ${n === 1 ? 'category' : 'categories'} changed`,
  deleted: (n) => `${n} ${n === 1 ? 'part' : 'parts'} removed`,
  specs_changed: (n) => `${n} ${n === 1 ? 'specification' : 'specifications'} updated`,
};

function isDelete(op: ModificationEditOperation): boolean {
  return op.type === 'delete' || op.type === 'bulk_delete';
}

/** Plain-language consequence of the delete policy, so nesting is never a surprise. */
function nestedConsequence(nested: number, policy: string): string {
  if (nested <= 0) return 'No nested parts are affected.';
  const parts = `${nested} nested ${nested === 1 ? 'part' : 'parts'}`;
  return policy === 'cascade'
    ? `${parts} will be removed as well.`
    : `${parts} will move up one level and stay in your build.`;
}

const OperationCard: React.FC<{
  op: ModificationEditOperation;
  checked: boolean;
  onToggle: (checked: boolean) => void;
  childPolicy: 'promote' | 'cascade';
}> = ({ op, checked, onToggle, childPolicy }) => {
  const preview = op.preview || {};
  const suggested = (preview.suggested_fields?.length ?? 0) > 0;
  const destructive = Boolean(op.destructive);

  return (
    <li>
      <label
        className={`card block cursor-pointer p-3.5 transition-colors ${
          checked ? 'border-white/[0.14]' : 'opacity-60 hover:opacity-100'
        }`}
      >
        <div className="flex items-start gap-3">
          <input
            type="checkbox"
            className="checkbox-control mt-1 h-4 w-4 shrink-0"
            checked={checked}
            onChange={(e) => onToggle(e.target.checked)}
          />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-semibold text-white mod-text-wrap">
                {preview.headline}
              </span>
              {destructive && (
                <span className="badge bg-red-500/10 text-red-300 border border-red-500/25">
                  Cannot be undone
                </span>
              )}
              {suggested && <AiSuggestedBadge />}
            </div>

            {preview.summary && !destructive && (
              <p className="mt-1 text-sm text-gray-400 mod-text-wrap">{preview.summary}</p>
            )}

            {/* Move: show where the part ends up, not just that it moved. */}
            {(preview.path_from || preview.path_to) && (
              <dl className="mt-2.5 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                {preview.path_from && (
                  <>
                    <dt className="text-meta pt-0.5">Now</dt>
                    <dd className="text-gray-400 mod-text-wrap">{preview.path_from}</dd>
                  </>
                )}
                {preview.path_to && (
                  <>
                    <dt className="text-meta pt-0.5">After</dt>
                    <dd className="text-white mod-text-wrap">{preview.path_to}</dd>
                  </>
                )}
              </dl>
            )}

            {/* Field and specification edits read as a before/after pair. */}
            {(preview.before || preview.after) && (
              <dl className="mt-2.5 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
                {preview.before && (
                  <>
                    <dt className="text-meta pt-0.5">Now</dt>
                    <dd className="text-gray-500 line-through mod-text-wrap">{preview.before}</dd>
                  </>
                )}
                {preview.after && (
                  <>
                    <dt className="text-meta pt-0.5">After</dt>
                    <dd className="text-white mod-text-wrap">{preview.after}</dd>
                  </>
                )}
              </dl>
            )}

            {destructive && (
              <p className="mt-2 text-sm text-gray-400">
                {nestedConsequence(preview.nested_count ?? 0, childPolicy)}
              </p>
            )}
          </div>
        </div>
      </label>
    </li>
  );
};

const ReviewSkeleton: React.FC = () => (
  <div className="space-y-3" aria-hidden>
    {[0, 1, 2].map((i) => (
      <div key={i} className="card animate-pulse p-3.5">
        <div className="flex items-start gap-3">
          <div className="mt-1 h-4 w-4 shrink-0 rounded bg-white/[0.08]" />
          <div className="flex-1 space-y-2">
            <div className="h-3.5 w-2/5 rounded bg-white/[0.08]" />
            <div className="h-3 w-4/5 rounded bg-white/[0.05]" />
          </div>
        </div>
      </div>
    ))}
  </div>
);

export const AiEditModificationsModal: React.FC<AiEditModificationsModalProps> = ({
  isOpen,
  vehicleId,
  onClose,
  onApplied,
}) => {
  const [phase, setPhase] = useState<Phase>('compose');
  const [instruction, setInstruction] = useState('');
  const [appliedInstruction, setAppliedInstruction] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [operations, setOperations] = useState<ModificationEditOperation[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [clarification, setClarification] = useState<any>(null);
  const [destructiveSummary, setDestructiveSummary] = useState<any>(null);
  const [hasNonUndoable, setHasNonUndoable] = useState(false);
  const [confirmDestructive, setConfirmDestructive] = useState(false);
  const [childPolicy, setChildPolicy] = useState<'promote' | 'cascade'>('promote');
  const [childPolicyTouched, setChildPolicyTouched] = useState(false);
  const [applySummary, setApplySummary] = useState<Record<string, number> | null>(null);
  const [applyBatchId, setApplyBatchId] = useState<string | null>(null);
  const [resolutionPath, setResolutionPath] = useState<string | null>(null);
  const [undoNotice, setUndoNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setPhase('compose');
      setInstruction('');
      setAppliedInstruction('');
      setError(null);
      setSessionToken(null);
      setOperations([]);
      setSelected({});
      setClarification(null);
      setDestructiveSummary(null);
      setHasNonUndoable(false);
      setConfirmDestructive(false);
      setChildPolicy('promote');
      setChildPolicyTouched(false);
      setApplySummary(null);
      setApplyBatchId(null);
      setResolutionPath(null);
      setUndoNotice(null);
      return;
    }
    try {
      void (apiService as any).trackAnalyticsEvent?.('mod_ai_edit_opened', { vehicle_id: vehicleId });
    } catch {
      // analytics optional
    }
  }, [isOpen, vehicleId]);

  const selectedIds = useMemo(
    () => Object.keys(selected).filter((id) => selected[id]),
    [selected]
  );

  const grouped = useMemo(
    () =>
      OP_GROUPS.map((group) => ({
        ...group,
        ops: operations.filter((op) => group.types.includes(op.type)),
      })).filter((group) => group.ops.length > 0),
    [operations]
  );

  const hasDeletes = useMemo(() => operations.some(isDelete), [operations]);
  const busy = phase === 'analyzing' || phase === 'applying';

  const requestClose = useCallback(async () => {
    // An applied session must survive so Undo stays reachable.
    if (sessionToken && phase !== 'done') {
      try {
        await (apiService as any).aiEditSession(vehicleId, sessionToken, 'discard');
      } catch {
        // best effort cleanup
      }
    }
    onClose();
  }, [sessionToken, phase, vehicleId, onClose]);

  if (!isOpen) return null;

  const ingestSession = (data: any) => {
    setSessionToken(data.session_token);
    setResolutionPath(data.resolution_path || null);
    setDestructiveSummary(data.destructive_summary || null);
    setHasNonUndoable(Boolean(data.has_non_undoable_ops));
    if (data.status === 'needs_clarification') {
      setClarification(data.clarification);
      setPhase('clarify');
      return;
    }
    const ops: ModificationEditOperation[] = Array.isArray(data.operations) ? data.operations : [];
    setOperations(ops);
    const nextSel: Record<string, boolean> = {};
    ops.forEach((op) => {
      nextSel[op.operation_id] = true;
    });
    setSelected(nextSel);
    // Start from the policy the proposal was previewed with, otherwise an explicit
    // "and everything inside" request would silently apply as promote.
    const proposedPolicy = ops.find(isDelete)?.preview?.child_policy;
    setChildPolicy(proposedPolicy === 'cascade' ? 'cascade' : 'promote');
    setChildPolicyTouched(false);
    setClarification(null);
    setPhase('review');
  };

  const analyze = async () => {
    const text = instruction.trim();
    if (!text) return;
    setError(null);
    setAppliedInstruction(text);
    setPhase('analyzing');
    try {
      const data = await (apiService as any).aiEditAnalyze(vehicleId, text);
      ingestSession(data);
    } catch (e: any) {
      setError(e?.message || 'Could not work out that change. Try describing it another way.');
      setPhase('error');
    }
  };

  const chooseClarification = async (option: any) => {
    setError(null);
    setPhase('analyzing');
    try {
      const data = await (apiService as any).aiEditAnalyze(vehicleId, appliedInstruction, {
        session_token: sessionToken,
        selected_option_id: option.id,
        selected_target_ids: option.target_ids || [],
      });
      ingestSession(data);
    } catch (e: any) {
      setError(e?.message || 'Could not continue');
      setPhase('clarify');
    }
  };

  const apply = async () => {
    if (!sessionToken || selectedIds.length === 0) return;
    if (destructiveSummary?.warn_large && !confirmDestructive) {
      setError('Confirm the large removal before applying.');
      return;
    }
    setError(null);
    setPhase('applying');
    try {
      const data = await (apiService as any).aiEditApply(vehicleId, sessionToken, selectedIds, {
        idempotency_key: `ai-edit-${sessionToken}-${selectedIds.join(',')}`,
        // Only override when the owner actually chose a policy, so the stored
        // per-operation policy stays authoritative.
        ...(childPolicyTouched ? { child_policy: childPolicy } : {}),
      });
      setApplySummary(data.summary || null);
      setApplyBatchId(data.apply_batch_id || null);
      setPhase('done');
      onApplied?.();
    } catch (e: any) {
      setError(e?.message || 'Could not apply those changes');
      setPhase('review');
    }
  };

  const undo = async () => {
    if (!sessionToken) return;
    setError(null);
    setUndoNotice(null);
    try {
      const result = await (apiService as any).aiEditUndo(
        vehicleId,
        sessionToken,
        applyBatchId || undefined
      );
      onApplied?.();
      const skipped = Array.isArray(result?.skipped) ? result.skipped.length : 0;
      const conflicts = Array.isArray(result?.field_conflicts) ? result.field_conflicts.length : 0;
      if (result?.undone === false) {
        setError('Nothing could be undone. Those parts changed again after this was applied.');
        return;
      }
      if (skipped > 0 || conflicts > 0) {
        const count = skipped + conflicts;
        setUndoNotice(
          `Undo finished, but ${count} change${count === 1 ? '' : 's'} could not be reversed. Removals are never restored, and parts edited since apply were left as they are.`
        );
        return;
      }
      onClose();
    } catch (e: any) {
      setError(e?.message || 'Could not undo');
    }
  };

  const setAllSelected = (value: boolean) => {
    const next: Record<string, boolean> = {};
    operations.forEach((op) => {
      next[op.operation_id] = value;
    });
    setSelected(next);
  };

  const appliedLines = applySummary
    ? Object.entries(applySummary)
        .filter(([, n]) => Number(n) > 0)
        .map(([key, n]) => SUMMARY_LABELS[key]?.(Number(n)) || `${key.replace(/_/g, ' ')}: ${n}`)
    : [];

  const footer = (() => {
    if (phase === 'compose' || phase === 'error') {
      return (
        <button
          type="button"
          className="btn-primary w-full"
          disabled={!instruction.trim()}
          onClick={() => void analyze()}
        >
          Review changes
        </button>
      );
    }
    if (phase === 'review') {
      return (
        <div className="flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            className="btn-secondary sm:flex-1"
            onClick={() => setPhase('compose')}
          >
            Edit instruction
          </button>
          <button
            type="button"
            className="btn-primary sm:flex-1"
            disabled={selectedIds.length === 0}
            onClick={() => void apply()}
          >
            {selectedIds.length === 0
              ? 'Select a change'
              : `Apply ${selectedIds.length} change${selectedIds.length === 1 ? '' : 's'}`}
          </button>
        </div>
      );
    }
    if (phase === 'done') {
      return (
        <div className="flex flex-col gap-2 sm:flex-row">
          <button type="button" className="btn-secondary sm:flex-1" onClick={() => void undo()}>
            Undo
          </button>
          <button type="button" className="btn-primary sm:flex-1" onClick={onClose}>
            Done
          </button>
        </div>
      );
    }
    if (phase === 'clarify') {
      return (
        <button type="button" className="btn-secondary w-full" onClick={() => setPhase('compose')}>
          Start over
        </button>
      );
    }
    // Keep the footer mounted while working so the panel does not resize mid-flow.
    return (
      <button type="button" className="btn-primary w-full" disabled>
        <span className="spinner-sm" aria-hidden />
        {phase === 'applying' ? 'Applying' : 'Reviewing your build'}
      </button>
    );
  })();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => void requestClose()}
      title="Edit with AI"
      description="Describe the change in your own words. Nothing is applied until you review it."
      size="lg"
      variant="sheet"
      closeOnEscape={!busy}
      closeOnOverlayClick={!busy}
      footer={footer}
    >
      <div className="space-y-4">
        {error && (
          <div className="alert-error" role="alert">
            {error}
          </div>
        )}

        {(phase === 'compose' || phase === 'error') && (
          <>
            <div>
              <label htmlFor="ai-edit-instruction" className="input-label">
                What should change?
              </label>
              <textarea
                id="ai-edit-instruction"
                className="input input-textarea min-h-[120px]"
                placeholder="Make the MIVEC head a child of the 2.2L engine"
                maxLength={MAX_INSTRUCTION}
                value={instruction}
                onChange={(e) => setInstruction(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && instruction.trim()) {
                    e.preventDefault();
                    void analyze();
                  }
                }}
              />
              <div className="flex items-start justify-between gap-3">
                <p className="input-helper">You can ask for several changes at once.</p>
                {instruction.length > MAX_INSTRUCTION * 0.8 && (
                  <p className="input-helper tabular-nums shrink-0">
                    {instruction.length} / {MAX_INSTRUCTION}
                  </p>
                )}
              </div>
            </div>

            {!instruction.trim() && (
              <div>
                <p className="text-meta mb-2">Try one of these</p>
                <div className="flex flex-wrap gap-2">
                  {EXAMPLES.map((example) => (
                    <button
                      key={example.label}
                      type="button"
                      className="rounded-lg border border-white/[0.1] bg-white/[0.02] px-3 py-1.5 text-xs font-medium text-gray-300 transition-colors hover:border-white/20 hover:bg-white/[0.06] hover:text-white cursor-pointer"
                      onClick={() => setInstruction(example.text)}
                    >
                      {example.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {phase === 'analyzing' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-300" aria-live="polite">
              Working out what to change in your build
            </p>
            <ReviewSkeleton />
          </div>
        )}

        {phase === 'clarify' && clarification && (
          <div className="space-y-3">
            <p className="text-sm text-white">{clarification.prompt}</p>
            <ul className="space-y-2">
              {(clarification.options || []).map((opt: any) => {
                const count = Array.isArray(opt.target_ids) ? opt.target_ids.length : 0;
                return (
                  <li key={opt.id}>
                    <button
                      type="button"
                      className="card-interactive w-full px-3.5 py-3 text-left"
                      onClick={() => void chooseClarification(opt)}
                    >
                      <span className="block text-sm font-medium text-white mod-text-wrap">
                        {opt.label}
                      </span>
                      {count > 1 && (
                        <span className="text-meta">{count} parts</span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {phase === 'applying' && (
          <p className="text-sm text-gray-300" aria-live="polite">
            Applying your changes
          </p>
        )}

        {/* The plan stays on screen while applying so the owner keeps their context. */}
        {(phase === 'review' || phase === 'applying') && (
          <div
            className={`space-y-4 ${phase === 'applying' ? 'pointer-events-none opacity-60' : ''}`}
            aria-busy={phase === 'applying'}
          >
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3.5 py-2.5">
              <p className="text-meta">You asked</p>
              <p className="text-sm text-gray-200 mod-text-wrap">{appliedInstruction}</p>
              {resolutionPath === 'deterministic' && (
                <p className="text-meta mt-1">Matched directly from your build.</p>
              )}
            </div>

            {hasNonUndoable && (
              <div className="alert-warning">
                Removals cannot be undone. Everything else can be reversed after you apply.
              </div>
            )}

            {destructiveSummary?.warn_large && (
              <div className="alert-error">
                <p>
                  This removes {destructiveSummary.affected_count} of{' '}
                  {destructiveSummary.build_total} parts in your build (
                  {destructiveSummary.percent}%).
                </p>
                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox-control h-4 w-4"
                    checked={confirmDestructive}
                    onChange={(e) => setConfirmDestructive(e.target.checked)}
                  />
                  <span>I understand and want to continue</span>
                </label>
              </div>
            )}

            {hasDeletes && (
              <fieldset className="rounded-lg border border-white/[0.06] px-3.5 py-3">
                <legend className="px-1 text-xs font-medium text-gray-300">
                  Parts nested under a removal
                </legend>
                <div className="space-y-2">
                  {(
                    [
                      ['promote', 'Keep them and move them up one level'],
                      ['cascade', 'Remove them along with the parent'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="flex items-center gap-2 text-sm text-gray-200 cursor-pointer">
                      <input
                        type="radio"
                        name="child_policy"
                        className="checkbox-control h-4 w-4"
                        checked={childPolicy === value}
                        onChange={() => {
                          setChildPolicy(value);
                          setChildPolicyTouched(true);
                        }}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {operations.length > 0 && (
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-white">
                  {operations.length} proposed {operations.length === 1 ? 'change' : 'changes'}
                </h3>
                <button
                  type="button"
                  className="btn-ghost btn-sm"
                  onClick={() => setAllSelected(selectedIds.length !== operations.length)}
                >
                  {selectedIds.length === operations.length ? 'Clear all' : 'Select all'}
                </button>
              </div>
            )}

            {grouped.map((group) => (
              <section key={group.id} className="space-y-2">
                <h4 className="text-meta uppercase tracking-wide">{group.label}</h4>
                <ul className="space-y-2">
                  {group.ops.map((op) => (
                    <OperationCard
                      key={op.operation_id}
                      op={op}
                      checked={Boolean(selected[op.operation_id])}
                      onToggle={(checked) =>
                        setSelected((prev) => ({ ...prev, [op.operation_id]: checked }))
                      }
                      childPolicy={childPolicy}
                    />
                  ))}
                </ul>
              </section>
            ))}

            {operations.length === 0 && (
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3.5 py-6 text-center">
                <p className="text-sm text-white">No matching parts found</p>
                <p className="mt-1 text-sm text-gray-400">
                  Nothing in your build matched that instruction. Try naming the part or brand as it
                  appears in your list.
                </p>
              </div>
            )}
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-3">
            <p className="text-sm font-semibold text-white">Changes applied</p>
            {appliedLines.length > 0 && (
              <ul className="space-y-1 text-sm text-gray-300">
                {appliedLines.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            {undoNotice && (
              <div className="alert-warning" role="status">
                {undoNotice}
              </div>
            )}
            {hasNonUndoable && (
              <p className="text-meta">Removed parts are not restored by Undo.</p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AiEditModificationsModal;
